<template>
     <b-col v-bind="settings.container">
        <form @submit="save">
            <template>
                <b-tabs v-model="currentTab" lazy :key="version">
                    <b-tab v-for="group in fieldGroups" :key="group" :href="groupName(group)">
                        <template slot="title">
                            <span v-if="group == ''">{{ $t('default') }}</span>
                            <span v-else>{{ groupName(group) }}</span>
                        </template>
                        <component :is="wrapperComponentElement" v-bind="wrapperComponent">
                            <b-col cols="12" align-self="center" v-for="(value, property) in sortedFields" :key="property">
                                <b-row :class="{fieldEditing: visibleFieldSettings}" v-if="value.visible && value.group == group">
                                    <b-col :cols="visibleFieldSettings ? 10 : 12">
                                        <IdentityFields :label="$translateLabel(value.label, value.name)" :field="value" v-model="input" @error="fieldError" :extendErrors="httpErrors[value.key]" @input="cleanErrors" :store="$store" :context="context" :translator="$t"  page="model_create" v-on="$listeners">
                                            <template v-slot:IdentityFieldSelectRelation v-if="value.module == 'select' && value.type == 'relation' && !settings.slotless">
                                                <SelectRelationUI :field="value" v-model="input" :key="value.key"/>
                                            </template>

                                            <template v-slot:IdentityFieldImageCarousel="{ itemSelected, mode }" v-if="value.module == 'image' && value.type == 'carousel' && !settings.slotless">
                                                <MediaLibrary @itemsSelected="itemSelected" :mode="mode" />
                                            </template>

                                            <template v-slot:ProvidedTemplate>
                                                <ComponentView :id="value.provided_template_component" :entrypoint="value.component_template" :context="context" :field="value" v-model="input" :key="value.key" page="model_create" v-on="$listeners"/>
                                            </template>
                                        </IdentityFields>
                                    </b-col>

                                    <b-col cols="1" offset="1"  align-self="center">
                                        <b-icon icon="gear-wide" v-if="visibleFieldSettings" @click="startFieldEdit(value)"/>
                                    </b-col>
                                </b-row>
                            </b-col>
                        </component>
                    </b-tab>
                </b-tabs>
                <div class="row">
                    <slot name="controls" :save="save" :saveButtonBusy="saving">
                        <div class="col-sm-2">
                            <b-button type="submit" variant="success" class="m-2" v-on:click="save" :disabled="saving"> {{ $t('save') }} </b-button>
                        </div>

                        <div class="col-sm-3" v-if="!isQuickCreate()">
                            <b-button type="submit" class="m-2" variant="primary" v-on:click="save"  :disabled="saving"> {{ $t('saveContinue') }} </b-button>
                        </div>
                    </slot>
                </div>
            </template>
        </form>

        <b-sidebar id="template" title="Fields"  right v-model="visibleFieldSettings">
            <DraggableFields :group="{ name: 'fields', pull: 'clone', put: false }" :mutateClone="onClone"/>
        </b-sidebar>

        <b-sidebar right v-model="editingField.show">
            <FieldEdit v-model="editingField.field" :key="version" v-if="editingField.field" :fields="fields" :component="component"></FieldEdit>

            <template #footer>
                <div class="d-flex bg-dark text-light align-items-center px-3 py-2">
                    <b-button variant="success" size="sm" @click="editField">{{ $t('save') }}</b-button>
                    <b-button variant="danger" size="sm" @click="removeField" class="ml-1">{{ $t('delete') }}</b-button>
                </div>
            </template>
        </b-sidebar>

        <b-modal title="Performing action" v-model="actionModal.processing"
        o-close-on-esc no-close-on-backdrop  hide-header-close hide-footer
        >
            <div class="d-flex align-items-center">
                {{ actionModal.actionName }}
                <b-spinner variant="primary" class="ml-4"></b-spinner>
            </div>
        </b-modal>

        <protokol-floating-settings v-if="$can(`settings ${component.general.name}`) && $isPlatformEnv('dev')">
            <template v-slot="{toggleSettings}">
                <b-button variant="dark" @click="visibleFieldSettings = true; toggleSettings()"> <b-icon icon="gear" /> </b-button>
            </template>
        </protokol-floating-settings>
    </b-col>
</template>

<script>
import { 
    Thunder,
    Context,
    Model,
} from '@protokol/platform-libs'

import IdentityFields from '@/components/identityFields/IdentityFields'
import safeEval from 'safe-eval'
import _ from "lodash"
import moment from 'moment'
import draggable from 'vuedraggable'
import DraggableFields from './Settings/DraggableFields.vue'
import FieldEdit from './Settings/FieldEdit.vue'
import MediaLibrary from '@/components/integrations/protokol/media/Index'

export default {
    components: {
    IdentityFields,
    SelectRelationUI: () => import("../identityFields/ui/SelectRelation.vue"),
    Fields: () => import("./Settings/Fields.vue"),
    ComponentView: () => import("./ComponentView.vue"),
    draggable,
    DraggableFields,
    FieldEdit,
    MediaLibrary
},
    props: {
        id: {
            type: String,
            default: null
        },

        component: {
            type: Object
        },

        externalModel: {
            type: Object
        },

        settings: {
            type: Object,
            default: () => {
                return {
                    container: {
                        cols: 12,
                        md: 6
                    },

                    slotless: false
                }
            }
        },

        noRedirectOnSave: {
            type: Boolean,
            default: false
        }
    },
    data: () => {
        return {
            model: {},
            aModel: {},
            errors: [],
            httpErrors: [],
            actionModal: {
                processing: false,
                actionName: ''
            },
            actionPopup: {
                show: false,
                action: null
            },
            visibleFieldSettings: false,
            version: 0,
            refreshingFields: false,
            saving: false,
            currentTab: 0,
            editingField: {
                show: false,
                field: null
            }
        }
    },

    computed: {
        input: {
            get() {
                return {...this.model, ...this.externalModel}
            },

            set(e) {
                if (typeof e !== 'object') {
                    console.error(`Invalid input. If you are trying to emit input event then you should try to pass an object. Ex: {field: value}`)
                    return
                }

                this.model = Object.assign({}, this.model, e)
                this.$emit('modelChanged', this.model)
            }
        },

        actionInput: {
            get() {
                return this.aModel
            },

            set(e) {
                this.aModel = {...this.aModel, ...e}
            }
        },

        sortedFields: {
            set(val) {
                this.component.settings.fields = val

                this.$store.dispatch("updateComponentSettings", this.component).then((settings) => {
                    this.component = settings
                })
            },

            get() {
                let fields = _.cloneDeep(this.fields || [])

                fields.sort((a, b) => {
                    return a.sortOrder - b.sortOrder
                })

                return fields
            }
        },

        fieldGroups() {
            try {
                let groups = {
                '': true
                }
                
                this.fields.forEach(f => {
                    const group = f.group || ''
                    
                    groups[group] = true
                })

                return Object.keys(groups);
            } catch(err) {
                this.$debugLog(err)

                return ['']
            }
        },

        context() {
            return new Context({
                model: _.cloneDeep(this.model), 
                locale: this.$store.getters.getLocale
            })
        },

        fields() {
            return this.component.settings.fields
        },

        wrapperComponent() {
            if (this.visibleFieldSettings) {
                return {
                    tag: 'div',
                    class: {
                        row: true
                    },
                    'v-model': this.sortedFields,
                    group: "fields"
                }
            }

            return {}
        },

        wrapperComponentElement() {
            if (this.visibleFieldSettings) {
                return 'draggable'
            }

            return 'b-row'
        }
    },
    methods: {
        fieldError(error) {
            let found = this.errors.find((i, e) => {
                return i.field == error.field
            })

            if (found === undefined) {
                this.errors.push(error)
            } else {
                this.errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },

        onClone(original) {
            original.name = `Field${this.sortedFields.length}`
            original.key = `field${this.sortedFields.length}`
            original.group = this.fieldGroups[this.currentTab]

            return original;
        },

        removeField() {
            this.$bvModal.msgBoxConfirm('Are you sure?').then((value) => {
                if (value) {
                    const fields = this.sortedFields.filter(f => {
                        return f.key != this.editedField.key
                    })

                    this.sortedFields = fields
                    this.editingField = false
                }
            })
        },

        editField() {
            let field = this.editingField.field
            let oldField = this.editingField.fieldOld

            let fields = this.sortedFields.map(el => {
                if (el.key == oldField.key) {
                return field
                } else {
                return el
                }
            });

            this.editingField.show = false

            this.sortedFields = fields

            this.version++
        },

        startFieldEdit(field) {
            this.editingField.show = true
            this.editingField.field = field
            this.editingField.fieldOld = _.cloneDeep(field)
        },

        actionClick(action) {
            let { fields = [] } = action

            if (fields === null) {
                this.doAction(action)
                return
            }

            if (fields.length > 0) {
                this.openActionPopup(action)
            } else {
                this.doAction(action)
            }
        },

        allSortedFields() {
            return this.sortedFields
        },

        openActionPopup(action) {
            this.actionPopup.show = true;
            this.actionPopup.action = action;
        },

        groupName(group) {
            return _.capitalize(group)
        },

        checkForErrors() {
            return this.errors.find((i, e) => {
                return i.error
            })
        },

        conditionalDisplay(f) {
            if (f.constraints.display_condition == "") {
                return true;
            }

            let display = safeEval('(function() {' + f.constraints.display_condition + '}());', {
                context: this.context
            })

            return display
        },

        save(e) {
            e.preventDefault()

            if (this.checkForErrors()) {
                this.$bvToast.toast(this.$t('someFieldsAreNotValid'), {
                    title: this.$t('error'),
                    variant: 'danger'
                })

                return
            }

            const popup = this.$loadingPopup()
            let id = this.getComponentID()
            this.$component(id).store().create(this.model).then((resp) => {
                this.$bvToast.toast(this.$t('Saved'), {
                    variant: 'success',
                    title: this.$t('success')
                })

                this.$emit('created', resp.data)

                //this.$store.commit("SET_COMPONENTS_MODEL", {uuid: componentUuid, results: resp.data})
                if (!this.noRedirectOnSave) {
                    this.$router.push({path: "/admin/component/" + id.replaceAll("::", "/") + "/edit/" + resp.data.uuid})
                }


            }).catch((error) => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    if (error.response.status == 417) {
                        this.httpErrors = error.response.data.errors

                        this.$bvToast.toast(this.$t('someFieldsAreNotValid'), {
                            title: this.$t('error'),
                            variant: 'danger'
                        })

                        return
                    }

                    this.$bvToast.toast(error.response.data.message, {
                        title: this.$t('error'),
                        variant: 'danger'
                    })
                }
            }).finally(() => {
                popup.hide()
            })
        },

        doAction(action) {
            this.actionModal.processing = true;
            this.actionModal.actionName = action.name;

            let cont = safeEval("(async function() { try {"+action.action+" return model;} catch (error) { return error } }());", {
                model: new Model(this.model, {locale: this.$store.getters.getLocale}),
                _: _,
                moment: moment,
                input: this.aModel,
                resource: new Thunder(this.$store)
            })

            Promise.resolve(cont).then(val => {
                if (val && val.stack && val.message) {
                    this.actionModal.processing = false
                    this.$bvModal.msgBoxOk("Action "+ action.name +" evaluation error: " + val.message)
                    return
                }

                if (val !== undefined) {
                    this.actionModal.processing = false;
                    this.model = val
                }

            }).catch(err => {
                console.log(err)
            })

        },

        cleanErrors() {
            this.httpErrors = []
        },

        getComponentID() {
            return this.id || this.$route.meta.uuid
        },

        isQuickCreate() {
            return this.id !== null
        },

        lgSize() {
            return this.isQuickCreate() ? 12 : 6
        },

        _: () => {
            return _
        },
    },

    updated() {
        console.log("[Render] DefaultCreate rendered")
    },

    created() {
        // this means this component is laoded as quick create
        if (!this.isQuickCreate()) {
            if (!this.$can("create " + this.component.general.name)) {
                this.$router.back()
            }
        }
    },
}
</script>

<style lang="scss">
.fieldEditing {
    border-bottom: 10px solid #2C3135;
    transition: border-width 0.5s linear;
    margin-left: -17px;
    margin-right: -17px;
}
</style>